import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, Observable, throwError } from 'rxjs';
import { switchMap, catchError, shareReplay, tap } from 'rxjs/operators';
import { JwtService } from 'src/app/core/services/jwt.service/jwt.service';
import { AuthService } from 'src/app/services/auth.service/auth.service';
import { CacheService } from 'src/app/services/cache.service/cache.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private refreshToken$: Observable<any> | null = null;

  constructor(
    private authService: AuthService,
    private jwtService: JwtService,
    private router: Router
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (this.isWhiteEndpoint(req.url)) {
      return next.handle(req);
    }

    const token = CacheService.GET_ACCESS_TOKEN();
    const expired = !token || this.jwtService.isTokenExpired(token);


    if (expired) {
      return this.doRefresh(req, next);
    }

    return next.handle(this.addToken(req, token!)).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.doRefresh(req, next);
        }
        return throwError(() => error);
      })
    );
  }

  private doRefresh(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.refreshToken$) {
      this.refreshToken$ = this.authService.refresh().pipe(
        tap((data) => {
          this.refreshToken$ = null;
        }),
        shareReplay(1)
      );
    } else {
    }

    return this.refreshToken$.pipe(
      switchMap((data: any) => {
        return next.handle(this.addToken(req, data.token)).pipe(
          catchError((retryError: HttpErrorResponse) => {
            if (retryError.status === 401) {
              this.refreshToken$ = null;
              this.router.navigate(['/auth/login']);
              return EMPTY;
            }
            return throwError(() => retryError);
          })
        );
      }),
      catchError((refreshError) => {
        this.refreshToken$ = null;
        this.router.navigate(['/auth/login']);
        return EMPTY;
      })
    );
  }

  private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  private isWhiteEndpoint(url: string): boolean {
    const whitelist = [
      '/api/account/login',
      '/api/account/register',
      '/api/account/refresh',
      '/api/pathologies',
      '/api/patient/second-opinions',
      '/api/advice-second',
      '/api/patient/second-opinions/verify',
      '/api/patients/appointments/',
      '/api/patientnologged/patient/appointments/',
      '/api/second-opinion-nologged/invoices/patient/invoicesClic',
      '/api/advice-second/invoices/patient/invoicesClic',
      '/auth/callback/google',
      '/api/account/login/google',

      '/fileuploads'
    ];

    let path: string;
    try {
      path = new URL(url, window.location.origin).pathname;
    } catch {
      path = url;
    }

    const isExactWhite = whitelist.some(endpoint => path === endpoint || path.endsWith(endpoint));
    const isDoctorPath = path.includes('/api/doctors/');
    const isSpecialities = path.includes('/api/specialities/');
    const isPatientPath = path.includes('/api/patients/appointments/');
    const isSecondOpinionPath = path.includes('/api/patient/second-opinions/');
    const isSecondAdvicePath = path.includes('/api/advice-second/');
    const isPatientNoLoggedPath = path.includes('/api/patientnologged/patient/appointments/');
    const isInvoisPath = path.includes('/api/patientnologged/patient/invoices/');
    const isPaymentPath = path.includes('/api/second-opinion-nologged/invoices/patient/invoicesClic');
    const isSecondAdvicePaymentPath = path.includes('/api/advice-second/invoices/patient/invoicesClic');
    const isConfrimRegisterPath = path.includes('/api/account/confirm');
    const googleAuthPath = path.includes('/auth/callback/google');
    const loginWithGooglePath = path.includes('/api/account/login/google');
    return isExactWhite || isDoctorPath || isSpecialities || isPatientPath
      || isPatientNoLoggedPath || isInvoisPath || isSecondOpinionPath
      || isPaymentPath || isConfrimRegisterPath || isSecondAdvicePath || isSecondAdvicePaymentPath || googleAuthPath || loginWithGooglePath;
  }
}